import { getUserPermissions, isMainMenuAllowed, isSubMenuAllowed } from './rolePermissions';

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const makeModuleName = (groupLabel, itemLabel) => `${slugify(groupLabel)}.${slugify(itemLabel)}`;

export const getReadableModulesSet = () => {
  try {
    const raw = localStorage.getItem('roleModules');
    if (!raw) return null;
    let arr = JSON.parse(raw);
    // Handle case where data might be nested in { modules: [...] } or just [...]
    if (!Array.isArray(arr) && arr?.modules) arr = arr.modules;
    if (!Array.isArray(arr)) return null;

    const readable = new Set();
    arr.forEach((m) => {
      const name = (m?.name || m?.moduleName || '').toString().toLowerCase().trim();
      if (!name) return;
      const actionsRaw = m?.actions || m?.permissions;
      const actions = Array.isArray(actionsRaw)
        ? actionsRaw
        : typeof actionsRaw === 'string'
          ? [actionsRaw]
          : [];
      const hasRead = actions.some((a) => {
        const actionStr = String(a || '').toLowerCase().trim();
        return actionStr === 'read' || actionStr === 'view' || actionStr === 'all';
      });
      if (hasRead) readable.add(name);
    });
    return readable;
  } catch {
    return null;
  }
};

export const getWritableModulesSet = () => {
  try {
    const raw = localStorage.getItem('roleModules');
    if (!raw) return null;
    let arr = JSON.parse(raw);
    if (!Array.isArray(arr) && arr?.modules) arr = arr.modules;
    if (!Array.isArray(arr)) return null;

    const writable = new Set();
    arr.forEach((m) => {
      const name = (m?.name || m?.moduleName || '').toString().toLowerCase().trim();
      if (!name) return;
      const actionsRaw = m?.actions || m?.permissions;
      const actions = Array.isArray(actionsRaw)
        ? actionsRaw
        : typeof actionsRaw === 'string'
          ? [actionsRaw]
          : [];
      const hasWrite = actions.some((a) => {
        const actionStr = String(a || '').toLowerCase().trim();
        return actionStr === 'write' || actionStr === 'edit' || actionStr === 'create' || actionStr === 'all';
      });
      if (hasWrite) writable.add(name);
    });
    return writable;
  } catch {
    return null;
  }
};

export const hasReadPermission = (moduleName) => {
  const userRole = getUserRole();
  if (userRole === 'superadmin') return true;

  const readable = getReadableModulesSet();
  if (!readable) return false;
  return readable.has(moduleName.toLowerCase().trim());
};

export const hasWritePermission = (moduleName) => {
  const userRole = getUserRole();
  if (userRole === 'superadmin') return true;
  if (userRole === 'admin' && moduleName.toLowerCase().trim() === 'user-management.create-roles') return true;

  const writable = getWritableModulesSet();
  if (!writable) return false;
  return writable.has(moduleName.toLowerCase().trim());
};

const filterRecursive = (node, parentLabel = null, sectionLabel = null, readableModules, parentAllowed = false) => {
  if (!node || !node.label) return null;

  // Always show Dashboard
  if (node.label === 'Dashboard') return node;

  const label = node.label;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  
  // Determine the groups for module name calculation
  // Section -> Group -> Child -> Grandchild
  const nextSection = sectionLabel || label;
  const nextGroup = parentLabel || (sectionLabel ? label : null);
  
  const slugifiedLabel = slugify(label);
  const modName = node.permission 
    ? node.permission.toLowerCase().trim()
    : `${slugify(nextGroup || label)}.${slugify(label)}`;

  // A node is allowed if it has explicit permission OR its slugified label matches
  // OR if its parent already passed the permission check
  let isThisNodeAllowed = readableModules.has(modName) || readableModules.has(slugifiedLabel) || parentAllowed;
  
  // Special bypass for Activity Logs
  if (label === 'Activity Logs') {
    const role = getUserRole();
    if (role === 'admin' || role === 'superadmin') isThisNodeAllowed = true;
  }
  
  if (!hasChildren) {
    // Leaf node validation
    return isThisNodeAllowed ? node : null;
  }

  // Node is a group/branch
  const filteredChildren = node.children
    .map((child) => filterRecursive(child, nextGroup, nextSection, readableModules, isThisNodeAllowed))
    .filter(Boolean);

  // If no children are permitted and the group itself doesn't allow access to anything, hide it
  if (filteredChildren.length === 0) return null;
  
  return { ...node, children: filteredChildren };
};

const filterByModules = (items, readableModules) => {
  return (items || [])
    .map((item) => {
      if (!item) return null;

      // Specialized bypass
      if (item?.label === 'Green Energy Management') return item;

      // Type heading bypass (will be filtered if no allowed siblings follow in some systems, 
      // but here we filter item by item)
      if (item.type === 'heading') return item;

      return filterRecursive(item, null, null, readableModules);
    })
    .filter(Boolean);
};

export const getUserRole = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      const rawRole = userData.role || userData.roleKey || '';
      const norm = normalizeRole(rawRole);
      return norm || 'employee';
    }
  } catch (error) {
    console.log('Error getting user role:', error);
  }
  return 'employee';
};

function normalizeRole(role) {
  if (!role || typeof role !== 'string') return '';
  const r = role.trim().toLowerCase();
  if (r === 'superadmin' || r === 'super admin') return 'superadmin';
  if (r === 'admin' || r === 'administrator') return 'admin';
  if (r === 'manager') return 'manager';
  if (r === 'employee' || r === 'staff') return 'employee';
  if (r === 'associate' || r === 'sales associate' || r === 'salesassociate') return 'associate';
  return '';
}

// Filter menu based on user role
export const filterMenuByRole = (menuItems, userRole) => {
  const userPermissions = getUserPermissions(userRole);

  return menuItems.filter(item => {

    // Always show headings
    if (item.type === 'heading') return true;

    // Check if main menu is allowed
    if (!isMainMenuAllowed(item, userPermissions)) {
      return false;
    }

    // Filter children if they exist
    if (item.children) {
      const filteredChildren = item.children.filter(child => {
        const parentMenu = item.label?.toLowerCase().replace(/\s+/g, '');

        // Check if this child is allowed
        if (!isSubMenuAllowed(child, parentMenu, userPermissions)) {
          return false;
        }

        // If this child has its own children, filter them recursively
        if (child.children) {
          const filteredGrandChildren = child.children.filter(grandChild => {
            const grandParentMenu = child.label?.toLowerCase().replace(/\s+/g, '');
            return isSubMenuAllowed(grandChild, grandParentMenu, userPermissions);
          });

          child.children = filteredGrandChildren;
          // If no grandchildren remain, still show child if it has a direct link
          return child.children.length > 0 || child.link;
        }

        return true;
      });

      item.children = filteredChildren;
      // If no children remain, still show parent if it has a direct link
      return item.children.length > 0 || item.link;
    }

    return true;
  });
};

// Get filtered structure based on current user role
export const getFilteredStructure = (structure) => {
  const readableModules = getReadableModulesSet();
  if (readableModules) {
    return filterByModules(structure, readableModules);
  }
  const userRole = getUserRole();
  // If user is superadmin, show everything
  if (userRole === 'superadmin') return structure;
  return filterMenuByRole(structure, userRole);
};
